import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/WarningAmber";
import {
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableContainer,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Claim as CaseModelClaim,
  ClaimTypes,
  Damage,
  IssueRelation,
  Quantum,
} from "../../shared/CaseModel";
import randomId from "../../shared/randomId";
import { IssueEditorDialog } from "../IssueEditorDialog";
import {
  ClaimedDamage,
  QuantumEditorDialog,
  QuantumEditorProps,
} from "../QuantumEditorDialog";
import { YesNoDialog } from "../general/YesNoDialog";
import { ClaimEditorDialog } from "./ClaimEditorDialog";
import {
  ClaimWinChanceCallbacks,
  ClaimWinChanceEditor,
} from "./ClaimWinChanceEditor";
import {
  Claim,
  Issue,
  IssueGroup,
  NO_ROW_CHANCES,
  Party,
  RowChances,
  involveDefendant,
  involvedDefendantIds,
  setInvolvedDefendants,
  switchToSpecific,
} from "./IssueModel";
import { QuantumEditorRows, QuantumTableCallbacks } from "./QuantumEditorRows";
import SettledDefendants from "./SettledDefendants";
import { GuideMarker } from "../guide/GuideMarker";
import { TwoLevelIssueHierarchyEditor } from "./TwoLevelIssueHierarchyEditor";
import ClaimTypeChip from "./ClaimTypeChip.tsx";
import { useCase } from "../../context/CaseContext";
import { useTheme } from "../../context/ThemeContext";

export interface ClaimCallbacks {
  // with a damage to save along, where an amount's damage is new or renamed
  onClaimUpdate: (claim: Claim, damage?: Damage) => void;
  onClaimDelete: (claim: Claim) => void;
}

export interface ClaimCardProps {
  claim: Claim;
  ordinal: string;
  callbacks: ClaimCallbacks;
  // all case parties should be available to be added to claim
  parties: Party[];
  // the damages of the case, with the claims that claim each: they name the
  // amounts, and the claimant's can be claimed here as well
  damages?: ClaimedDamage[];
  // leaf issues under this claim with no entered win chance, counted off the
  // stored model so a collapsed card still shows the gap
  winChancesMissing?: number;
  // this claim carries no claimed amount, so it is worth nothing if it
  // succeeds. Flagged on every such claim regardless of whether the case-level
  // prerequisite is already satisfied by another one.
  claimedAmountMissing?: boolean;
  // what the calculation makes of the claim, its issues and groups
  chances?: RowChances;
}

export const ClaimCard = ({
  claim,
  ordinal,
  callbacks,
  parties,
  damages = [],
  winChancesMissing = 0,
  claimedAmountMissing = false,
  chances = NO_ROW_CHANCES,
}: ClaimCardProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims",
  });
  // for the prerequisite wording, which other components share
  const { t: tRoot } = useTranslation();
  const { readOnly } = useCase();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newIssueEditorOpen, setNewIssueEditorOpen] = useState(false);
  const [editedIssue, setEditedIssue] = useState<Issue | undefined>();
  const [quantumEditorProps, setQuantumEditorProps] =
    useState<QuantumEditorProps>();

  // involvement, not entered chances: a defendant involved but not yet rated
  // must come back pre-selected, or saving the editor would drop them
  const getDefendantsOfIssue = (issue: Issue) => {
    const issueDefendantIds = involvedDefendantIds(issue);
    return claim.defendants.filter((d) => issueDefendantIds.includes(d.id));
  };
  const { theme } = useTheme();

  // a damage with the claims other than this one that claim it
  const elsewhere = ({ damage, claimedIn }: ClaimedDamage): ClaimedDamage => ({
    damage,
    claimedIn: claimedIn.filter(({ claimId }) => claimId !== claim.id),
  });
  const claimedDamageOf = (damageId: string): ClaimedDamage =>
    damages.find(({ damage }) => damage.id === damageId) ?? {
      damage: { id: damageId, claimantId: claim.claimant.id, name: damageId },
      claimedIn: [],
    };
  const damageIds = claim.quantums?.map(({ damageId }) => damageId) ?? [];

  const onQuantumEditorRequested = (quantum?: Quantum) => {
    const onClose: QuantumEditorProps["onClose"] = (result) => {
      setQuantumEditorProps(undefined);
      if (result === undefined) return;
      const { quantum: newQuantum, damage } = result;
      if (quantum === undefined) {
        // initialize quantum liability at 100% per claim defendant
        if (newQuantum.liabilityShares === undefined)
          newQuantum.liabilityShares = {};
        for (const { id } of claim.defendants) {
          newQuantum.liabilityShares[id] = 1;
        }
        claim.quantums = [...(claim.quantums ?? []), newQuantum];
      } else
        claim.quantums = claim.quantums?.map((q) =>
          q.damageId === newQuantum.damageId ? newQuantum : q,
        );
      callbacks.onClaimUpdate(claim, damage);
    };

    setQuantumEditorProps({
      edited: quantum && {
        quantum,
        ...elsewhere(claimedDamageOf(quantum.damageId)),
      },
      claimantId: claim.claimant.id,
      offeredDamages: damages
        .filter(
          ({ damage }) =>
            damage.claimantId === claim.claimant.id &&
            !damageIds.includes(damage.id),
        )
        .map(elsewhere),
      takenNames: damageIds
        .filter((damageId) => damageId !== quantum?.damageId)
        .map((damageId) => claimedDamageOf(damageId).damage.name),
      open: true,
      onClose,
    });
  };

  const quantumCallbacks: QuantumTableCallbacks = {
    onQuantumDelete: (quantum) => {
      const claimCopy: Claim = JSON.parse(JSON.stringify(claim));
      claimCopy.quantums = claimCopy.quantums?.filter(
        (q) => q.damageId !== quantum.damageId,
      );

      callbacks.onClaimUpdate(claimCopy);
    },
    onQuantumEditorRequested,
    onQuantumUpdate: (quantum) => {
      const claimCopy: Claim = JSON.parse(JSON.stringify(claim));
      claimCopy.quantums = claimCopy.quantums?.map((q) =>
        q.damageId === quantum.damageId ? quantum : q,
      );
      callbacks.onClaimUpdate(claimCopy);
    },
  };
  const hasIssues: boolean =
    claim.issues !== undefined && claim.issues.length > 0;
  const hasQuantums: boolean =
    claim.quantums !== undefined && claim.quantums.length > 0;

  const onIssuesUpdate = (newIssues: (Issue | IssueGroup)[]) => {
    claim.issues = newIssues;
    callbacks.onClaimUpdate(claim);
  };
  const parentClaim: CaseModelClaim = {
    id: claim.id,
    title: claim.title,
    claimant: claim.claimant,
    defendants: claim.defendants,
  };

  const createClaimCallbacks: (claim: Claim) => ClaimWinChanceCallbacks = (
    claim,
  ) => ({
    onWinChanceUpdated: (defendantId: string, newChance: number) => {
      const claimCopy: Claim = JSON.parse(JSON.stringify(claim));
      if (claimCopy.winChanceAgainst === undefined)
        claimCopy.winChanceAgainst = {};
      claimCopy.winChanceAgainst[defendantId] = newChance;
      callbacks.onClaimUpdate(claimCopy);
    },
    onSwitchToGeneric: () => {
      const claimCopy: Claim = JSON.parse(JSON.stringify(claim));
      claimCopy.generic = true;
      callbacks.onClaimUpdate(claimCopy);
    },
    onSwitchToSpecific: () => {
      const claimCopy: Claim = JSON.parse(JSON.stringify(claim));
      switchToSpecific(claimCopy, claimCopy.defendants);
      callbacks.onClaimUpdate(claimCopy);
    },
    onGenericWinChanceUpdated: (newChance: number) => {
      const claimCopy: Claim = JSON.parse(JSON.stringify(claim));
      claimCopy.genericWinChance = newChance;
      callbacks.onClaimUpdate(claimCopy);
    },
  });

  const claimType = claim.type ?? ClaimTypes.Other;
  const divider = `1px solid ${theme.palette.divider}`;
  const buttonSx = { textTransform: "none", fontWeight: 600 } as const;

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, overflow: "hidden", userSelect: "none" }}
    >
      <ClaimEditorDialog
        title={claim.title}
        claimant={claim.claimant}
        defendants={claim.defendants || []}
        parties={parties}
        open={editDialogOpen}
        type={claim.type as ClaimTypes}
        onClose={(update?) => {
          setEditDialogOpen(false);
          if (update === undefined) {
            return;
          }
          const updatedClaim = { ...claim, ...update };
          // make sure that all new defendants are set in issues
          const newDefendants = updatedClaim.defendants.filter(
            (d) => !claim.defendants.some((cd) => cd.id === d.id),
          );
          newDefendants.forEach((newDefendant) => {
            involveDefendant(newDefendant.id, claim.issues);
          });
          callbacks.onClaimUpdate(updatedClaim);
        }}
      />
      {newIssueEditorOpen && (
        <IssueEditorDialog
          onClose={(issue?) => {
            setNewIssueEditorOpen(false);
            if (!issue) return;

            const newIssue: Issue = {
              ...issue,
              id: randomId(),
              relation: IssueRelation.And,
              winChanceAgainst: {},
            };
            issue.defendants.forEach((defendant) =>
              involveDefendant(defendant.id, [newIssue]),
            );

            claim.issues.push(newIssue);
            callbacks.onClaimUpdate(claim);
          }}
          parentClaim={parentClaim}
          defendants={claim.defendants}
          parties={claim.defendants}
          open={newIssueEditorOpen}
        />
      )}
      {!newIssueEditorOpen && editedIssue !== undefined && (
        <IssueEditorDialog
          parentClaim={parentClaim}
          title={editedIssue.title}
          generic={editedIssue.generic}
          defendants={getDefendantsOfIssue(editedIssue)}
          open
          onClose={(issueTemplate) => {
            if (issueTemplate) {
              const newDefendantIds = issueTemplate.defendants.map((d) => d.id);
              const leavesGeneric =
                editedIssue.generic && !issueTemplate.generic;

              editedIssue.title = issueTemplate.title;
              editedIssue.generic = issueTemplate.generic;

              // leaving generic here starts the chosen defendants from the
              // generic chance, as the toggle does
              if (leavesGeneric)
                switchToSpecific(editedIssue, issueTemplate.defendants);
              else setInvolvedDefendants(editedIssue, newDefendantIds);

              callbacks.onClaimUpdate(claim);
            }
            setEditedIssue(undefined);
          }}
          parties={claim.defendants}
        />
      )}
      {quantumEditorProps && <QuantumEditorDialog {...quantumEditorProps} />}
      <YesNoDialog
        onYes={() => {
          callbacks.onClaimDelete(claim);
          setDeleteDialogOpen(false);
        }}
        onNo={() => {
          setDeleteDialogOpen(false);
        }}
        yesColor="error"
        open={deleteDialogOpen}
        title={t("deleteDialog.title")}
        description={t("deleteDialog.description", {
          claim: claim.title,
        })}
      />

      {/* What the claim is, between whom, and how far it is settled */}
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          px: 2.5,
          pt: 2,
          pb: 2.25,
          borderBottom: divider,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "text.secondary" }}
            >
              {t("number", { ordinal })}
            </Typography>
            <ClaimTypeChip
              type={claimType}
              label={t(`claimTypes.${claimType}`)}
            />
          </Stack>
          <Typography
            variant="h6"
            component="h2"
            sx={{ fontWeight: 700, letterSpacing: "-.01em", mt: 0.5 }}
          >
            {claim.title}
          </Typography>
          <Stack
            direction="row"
            spacing={2.75}
            useFlexGap
            sx={{
              flexWrap: "wrap",
              mt: 0.5,
              color: "text.secondary",
              "& b": { color: "text.primary", fontWeight: 600 },
            }}
          >
            <Typography variant="body2">
              <Trans
                i18nKey="case.claims.claimant"
                values={{ name: claim.claimant.name }}
                components={{ bold: <b /> }}
              />
            </Typography>
            <Typography variant="body2">
              <Trans
                i18nKey="case.claims.defendants"
                values={{
                  names: claim.defendants.map((d) => d.name).join(" & "),
                }}
                count={claim.defendants.length}
                components={{ bold: <b /> }}
              />
            </Typography>
            <GuideMarker entry="parties" />
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          {winChancesMissing > 0 && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              icon={<WarningIcon />}
              label={tRoot("case.prerequisites.claimWinChancesMissing", {
                count: winChancesMissing,
              })}
            />
          )}
          <GuideMarker entry="settled" />
          <SettledDefendants defendants={claim.defendants} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            disabled={readOnly}
            onClick={() => setEditDialogOpen(true)}
            title={t("tip.edit")}
            sx={buttonSx}
          >
            {t("edit")}
          </Button>
          <Tooltip title={t("tip.delete")}>
            {/* the tooltip needs an element that fires events, which a
                disabled button does not */}
            <span>
              <IconButton
                size="small"
                aria-label={t("tip.delete")}
                disabled={readOnly}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ "&:hover": { color: theme.palette.data.negative.dark } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Win chances and claimed amounts share the defendant columns */}
      <TableContainer>
        <Table
          size="small"
          sx={{
            // the first and the last column line up with the card's own inset
            "& .MuiTableCell-root:first-of-type": { pl: 2.5 },
            "& .MuiTableCell-root:last-of-type": { pr: 2.5 },
          }}
        >
          {hasIssues ? (
            <TwoLevelIssueHierarchyEditor
              claimId={claim.id}
              issues={claim.issues}
              defendants={claim.defendants}
              onIssuesUpdate={onIssuesUpdate}
              onIssueEditorRequested={setEditedIssue}
              chances={chances}
            />
          ) : (
            <ClaimWinChanceEditor
              claim={claim}
              defendants={claim.defendants}
              callbacks={createClaimCallbacks(claim)}
              chances={chances}
            />
          )}
          {hasQuantums && (
            <QuantumEditorRows
              claim={{ id: claim.id, title: claim.title, ordinal }}
              defendants={claim.defendants}
              callbacks={quantumCallbacks}
              quantums={claim.quantums ?? []}
              damages={damages.map(({ damage }) => damage)}
              firstColSpan={hasIssues ? 2 : 1}
            />
          )}
        </Table>
      </TableContainer>

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: "wrap", px: 2.5, py: 1.5, borderTop: divider }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          disabled={readOnly}
          onClick={() => setNewIssueEditorOpen(true)}
          title={t("issues.tip.add")}
          sx={buttonSx}
        >
          {t("addIssue")}
        </Button>
        {/* a claim with no amount is worth nothing if it succeeds, and the
            button is the only place to fix that — so mark the button */}
        <Badge
          color="warning"
          badgeContent="!"
          invisible={!claimedAmountMissing}
          // default badge is sized for an icon; on a text button it dominates
          sx={{
            "& .MuiBadge-badge": {
              height: 14,
              minWidth: 14,
              fontSize: 10,
              px: 0.25,
            },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            disabled={readOnly}
            onClick={() => onQuantumEditorRequested()}
            title={
              claimedAmountMissing
                ? tRoot("case.prerequisites.claimAmountMissing")
                : t("quantums.tip.add")
            }
            sx={buttonSx}
          >
            {t("addAmount")}
          </Button>
        </Badge>
      </Stack>
    </Paper>
  );
};
